// Choferes en vivo: la última posición de cada uno (GET
// /v2/driver-locations/last) y lo que se lee de ella. Funciones puras: las
// usan la pantalla, su lista y el dibujo del mapa (lib/mapDraw).
import { driverName } from "./driverName";

export type Chofer = {
  driver_id: number; username: string; first_name: string | null; last_name: string | null; vehiculo: string | null;
  latitude: number; longitude: number; accuracy_m: number | null; speed_mps: number | null; heading_deg: number | null;
  battery_level: number | null; recorded_at: string; received_at: string; source: string;
  tracking_mode: string | null; tracking_error: string | null; tracking_at: string | null;
  turbo_enabled?: boolean;
  // `turbo_enabled` es "puede recibir ofertas"; `turbo_route_id` es "lleva
  // uno AHORA". Son cosas distintas y el despachante mira la segunda.
  turbo_route_id?: number | null; turbo_deadline_at?: string | null;
  phone?: string | null;
};

export const SIN_SENAL_MIN = 5;
// Cuánto tardó la última posición en llegar desde que el teléfono la grabó.
// Más de esto es «subida con atraso»: el teléfono graba pero no puede mandar
// (red cortada en segundo plano); el punto se ve pero es viejo.
export const ATRASO_MIN = 5;

// Cuánto hace que el chofer reportó, en minutos.
export const minutosDesde = (iso: string) => Math.max(0, (Date.now() - new Date(iso).getTime()) / 60000);
export const atrasoMin = (c: { recorded_at: string; received_at: string }) =>
  Math.max(0, (new Date(c.received_at).getTime() - new Date(c.recorded_at).getTime()) / 60000);
export const conAtraso = (c: { recorded_at: string; received_at: string }) => atrasoMin(c) > ATRASO_MIN;
export const sinSenal = (c: Chofer) => minutosDesde(c.recorded_at) > SIN_SENAL_MIN;

export const COLOR: Record<string, string> = { background: "#4f8cff", foreground: "#3fb950", heartbeat: "#d29922", buffer: "#4f8cff" };
export const GRIS = "#6b7280";
export const TURBO = "#f0883e";
export const llevaTurbo = (c: Chofer) => c.turbo_route_id != null;
// Un chofer con turbo se pinta de turbo aunque esté sin señal: justamente
// ese es el caso que hay que ver primero (el punto igual se agranda y la
// lista lo sube arriba de todo).
export const colorDe = (c: Chofer) => (llevaTurbo(c) ? TURBO : sinSenal(c) ? GRIS : COLOR[c.source] ?? "#4f8cff");
export const soloDigitos = (t: string) => t.replace(/\D/g, "");
export const horaCorta = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "";
export const nombreChofer = (c: Chofer) => driverName(c);

export const FUENTE: Record<string, string> = { background: "segundo plano", foreground: "app abierta", heartbeat: "quieto (latido)", buffer: "subido del buffer" };
export const MODO: Record<string, string> = { background: "seguimiento en segundo plano", "foreground-only": "solo con la app abierta", denied: "sin permiso de ubicación", off: "GPS apagado" };

export function haceCuanto(iso: string) {
  const m = minutosDesde(iso);
  if (m < 1) return "ahora";
  if (m < 60) return `hace ${Math.round(m)} min`;
  const h = Math.floor(m / 60);
  return `hace ${h} h ${Math.round(m % 60)} min`;
}

export function choferTooltip(c: Chofer) {
  const turbo = llevaTurbo(c)
    ? ` · TURBO #${c.turbo_route_id}${c.turbo_deadline_at ? ` (entrega ${horaCorta(c.turbo_deadline_at)})` : ""}` : "";
  const atraso = conAtraso(c) ? ` · subió con ${Math.round(atrasoMin(c))} min de atraso` : "";
  return `${nombreChofer(c)}${turbo} · ${haceCuanto(c.recorded_at)} · ${FUENTE[c.source] ?? c.source}${atraso}`;
}

// Los números de arriba de la pantalla.
export function resumenChoferes(choferes: Chofer[]) {
  const enCalle = choferes.filter((c) => !sinSenal(c)).length;
  return {
    enCalle,
    sinSenal: choferes.length - enCalle,
    foregroundOnly: choferes.filter((c) => c.tracking_mode && c.tracking_mode !== "background").length,
    atrasados: choferes.filter((c) => !sinSenal(c) && conAtraso(c)).length,
    conTurbo: choferes.filter(llevaTurbo).length,
    turboSinSenal: choferes.filter((c) => llevaTurbo(c) && sinSenal(c)).length,
  };
}
export type ResumenChoferes = ReturnType<typeof resumenChoferes>;

// Primero el que lleva un turbo y encima no reporta: es el único caso que
// pide hacer algo YA. Después el resto de los turbos, y después por
// silencio (el que hace más que no reporta, arriba).
const prioridad = (c: Chofer) => (llevaTurbo(c) ? (sinSenal(c) ? 0 : 1) : 2);
export const ordenarChoferes = (choferes: Chofer[]) =>
  [...choferes].sort((a, b) => prioridad(a) - prioridad(b) || minutosDesde(b.recorded_at) - minutosDesde(a.recorded_at));
